import type { BoardDetail, Task } from "@/lib/types";

/** The cached `GET /boards` payload the board views read. */
export interface BoardsData {
  boards: BoardDetail[];
}

const GAP = 1024;

/** Mirrors core's `positionBetween`, so the card lands where the server will put it. */
function positionBetween(before: number | null, after: number | null): number {
  if (before === null && after === null) return GAP;
  if (before === null) return (after as number) - GAP;
  if (after === null) return before + GAP;
  return (before + after) / 2;
}

/**
 * The boards with one card moved to `index` in `columnId`, as core's `moveTask`
 * will leave them: the index counts the column's other cards (the card itself is
 * left out, as `positionAtIndex` does), clamped to the column, and the position is
 * the midpoint of its new neighbours. The rest of the board — counts, stats — is
 * left for the refetch that follows, which is a moment away.
 *
 * Returns the same object when the card is not there, so a stale drag is a no-op.
 */
export function applyTaskMove(data: BoardsData | undefined, taskId: string, columnId: string, index: number): BoardsData | undefined {
  if (!data) return data;
  const boardIndex = data.boards.findIndex((detail) => detail.tasks.some((task) => task.id === taskId));
  if (boardIndex === -1) return data;
  const detail = data.boards[boardIndex]!;
  if (!detail.columns.some((column) => column.id === columnId)) return data;

  const others = detail.tasks
    .filter((task) => task.columnId === columnId && task.id !== taskId)
    .sort((a, b) => a.position - b.position);
  const clamped = Math.max(0, Math.min(index, others.length));
  const before = clamped === 0 ? null : (others[clamped - 1]?.position ?? null);
  const after = clamped >= others.length ? null : (others[clamped]?.position ?? null);
  const position = positionBetween(before, after);

  const tasks = detail.tasks.map((task) => (task.id === taskId ? { ...task, columnId, position } : task));
  const boards = data.boards.map((entry, at) => (at === boardIndex ? { ...entry, tasks } : entry));
  return { ...data, boards };
}

/** The fields of a card edit the cache can show before the server answers. */
export interface TaskFieldPatch {
  title?: string;
  description?: string;
  assignee?: string | null;
  priority?: Task["priority"];
  dueAt?: string | null;
  project?: string | null;
  areas?: string[] | null;
  blockedReason?: string | null;
}

/**
 * The boards with one card's fields edited as `updateTask` will store them. Only
 * what maps one-to-one onto the row is applied (`assignee` is a user id from the
 * picker, `project` a project id or null for "inherit"); what the server derives
 * — a due date clamped to the board, stats — arrives with the refetch after it.
 *
 * Returns the same object when the card is not there.
 */
export function applyTaskPatch(data: BoardsData | undefined, taskId: string, patch: TaskFieldPatch): BoardsData | undefined {
  if (!data) return data;
  const boardIndex = data.boards.findIndex((detail) => detail.tasks.some((task) => task.id === taskId));
  if (boardIndex === -1) return data;
  const edit = (task: Task): Task => ({
    ...task,
    ...(patch.title !== undefined ? { title: patch.title } : {}),
    ...(patch.description !== undefined ? { description: patch.description || null } : {}),
    ...(patch.assignee !== undefined ? { assigneeId: patch.assignee } : {}),
    ...(patch.priority !== undefined ? { priority: patch.priority } : {}),
    ...(patch.dueAt !== undefined ? { dueAt: patch.dueAt } : {}),
    ...(patch.project !== undefined ? { projectId: patch.project } : {}),
    ...(patch.areas !== undefined ? { areas: patch.areas ?? [] } : {}),
    ...(patch.blockedReason !== undefined ? { blockedReason: patch.blockedReason } : {}),
  });
  const detail = data.boards[boardIndex]!;
  const tasks = detail.tasks.map((task) => (task.id === taskId ? edit(task) : task));
  const boards = data.boards.map((entry, at) => (at === boardIndex ? { ...entry, tasks } : entry));
  return { ...data, boards };
}
